
import React, { useState } from "react";
import { Plus, Palette, Layers, Share2, Download, Upload, Settings, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { EnhancedColorPicker } from "@/components/ui/enhanced-color-picker";
import { CustomSelect, CustomSelectTrigger, CustomSelectValue, CustomSelectContent, CustomSelectItem } from "@/components/ui/custom-select";
import { Node, Edge } from "@xyflow/react";
import ShapeSelector from "./ShapeSelector";
import FlowchartExport from "./FlowchartExport";
import ShareWhiteboard from "./ShareWhiteboard";
import WhiteboardTitleEditor from "./WhiteboardTitleEditor";
import { CommentsSystem } from "./CommentsSystem";
import { VersionHistory } from "./VersionHistory";
import { AutosaveIndicator } from "./AutosaveIndicator";
import { useAutosave } from "@/hooks/useAutosave";

interface ModernToolbarProps {
  onAddNode: () => void;
  selectedCategory: string;
  selectedShape: string;
  onCategoryChange: (category: string) => void;
  onShapeChange: (shape: string) => void;
  colorPickerValue: string;
  onColorChange: (color: string) => void;
  onChangeAllNodesColor: (color: string) => void;
  nodes: Node[];
  edges: Edge[];
  whiteboardName: string;
}

const categoryOptions = [
  { value: "process", label: "Process" },
  { value: "decision", label: "Decision" },
  { value: "data", label: "Data" },
  { value: "terminator", label: "Start/End" },
  { value: "connector", label: "Connector" },
  { value: "document", label: "Document" },
  { value: "storage", label: "Storage" },
  { value: "manual", label: "Manual" },
  { value: "display", label: "Display" },
];

const ModernToolbar: React.FC<ModernToolbarProps> = ({
  onAddNode,
  selectedCategory,
  selectedShape,
  onCategoryChange,
  onShapeChange,
  colorPickerValue,
  onColorChange,
  onChangeAllNodesColor,
  nodes,
  edges,
  whiteboardName,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Autosave functionality
  const { manualSave, hasUnsavedChanges, isAutosaveEnabled } = useAutosave({
    whiteboardId: "current", // You might want to pass the actual whiteboard ID
    nodes,
    edges,
    enabled: true,
  });

  return (
    <>
      <Card className="fixed top-4 left-4 right-4 z-50 bg-white/95 backdrop-blur-sm border shadow-lg">
        <div className="flex items-center justify-between p-3">
          {/* Left Section - Title and Node Tools */}
          <div className="flex items-center gap-3">
            <WhiteboardTitleEditor 
              whiteboardId="current"
              whiteboardName={whiteboardName}
              onChangeName={() => {}} // You might want to implement this
            />
            
            <Separator orientation="vertical" className="h-8" />
            
            <Button
              onClick={onAddNode}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Node
            </Button>

            <CustomSelect
              value={selectedCategory}
              onValueChange={onCategoryChange}
            >
              <CustomSelectTrigger className="w-32">
                <CustomSelectValue placeholder="Category" />
              </CustomSelectTrigger>
              <CustomSelectContent>
                {categoryOptions.map((option) => (
                  <CustomSelectItem key={option.value} value={option.value}>
                    {option.label}
                  </CustomSelectItem>
                ))}
              </CustomSelectContent>
            </CustomSelect>

            <ShapeSelector
              selectedShape={selectedShape}
              onShapeChange={onShapeChange}
            />
          </div>

          {/* Center Section - Autosave Indicator */}
          <AutosaveIndicator
            hasUnsavedChanges={hasUnsavedChanges()}
            isAutosaveEnabled={isAutosaveEnabled}
            onManualSave={manualSave}
          />

          {/* Right Section - Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
              className="gap-2"
            >
              <Users className="w-4 h-4" />
              Comments
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowVersions(!showVersions)}
              className="gap-2"
            >
              <Layers className="w-4 h-4" />
              Versions
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="gap-2"
            >
              <Palette className="w-4 h-4" />
              Colors
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExport(true)}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowShare(true)}
              className="gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>

            <Button variant="ghost" size="sm">
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Color Picker Panel */}
        {showColorPicker && (
          <div className="border-t p-4 bg-gray-50/50">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Element Color:</span>
                <EnhancedColorPicker
                  value={colorPickerValue}
                  onChange={onColorChange}
                />
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => onChangeAllNodesColor(colorPickerValue)}
                className="ml-auto"
              >
                Apply to All Nodes
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Comments System */}
      {showComments && (
        <CommentsSystem
          whiteboardId="current"
          onClose={() => setShowComments(false)}
        />
      )}

      {/* Version History */}
      {showVersions && (
        <VersionHistory
          whiteboardId="current"
          onClose={() => setShowVersions(false)}
          onLoadVersion={(nodes, edges) => {
            // You might want to implement this callback
            console.log('Loading version:', { nodes, edges });
          }}
        />
      )}

      {/* Export Modal */}
      {showExport && (
        <FlowchartExport
          open={showExport}
          onOpenChange={setShowExport}
          nodes={nodes}
          edges={edges}
          whiteboardName={whiteboardName}
        />
      )}

      {/* Share Modal */}
      {showShare && (
        <ShareWhiteboard
          open={showShare}
          onOpenChange={setShowShare}
          nodes={nodes}
          edges={edges}
          whiteboardName={whiteboardName}
        />
      )}
    </>
  );
};

export default ModernToolbar;
